import type { DropdownItem } from '@seek/ca-myprofile-utils';
import { format } from '@seek/currency';
import { salaryTypes as getSalaryTypes } from '@seek/profile-taxonomies';
import { useEffect, useState } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import type { Site } from 'src/types';
import * as Schema from 'src/types/graphql';

const seekSalaryOrder = [
  Schema.SalaryType2.Annual,
  Schema.SalaryType2.Hourly,
  Schema.SalaryType2.Monthly,
];

const asiaSalaryOrder = [
  Schema.SalaryType2.Monthly,
  Schema.SalaryType2.Hourly,
  Schema.SalaryType2.Annual,
];

const salaryOptionsBySite: Record<Site, Schema.SalaryType2[]> = {
  'candidate-seek-au': seekSalaryOrder,
  'candidate-seek-nz': seekSalaryOrder,
  'candidate-jobsdb-hk': asiaSalaryOrder,
  'candidate-jobsdb-th': asiaSalaryOrder,
  'candidate-jobstreet-id': asiaSalaryOrder,
  'candidate-jobstreet-my': asiaSalaryOrder,
  'candidate-jobstreet-sg': asiaSalaryOrder,
  'candidate-jobstreet-ph': asiaSalaryOrder,
};

export const useSalaryOptions = (site: Site): DropdownItem[] => {
  const { language } = useAppConfig();
  const [salaryOptions, setSalaryOptions] = useState<DropdownItem[]>([]);

  useEffect(() => {
    getSalaryTypes(language).then((salaryTypes) => {
      const siteSalaryOptions = salaryOptionsBySite[site];
      const translatedSalaryOptions = siteSalaryOptions.map((salaryOption) => ({
        value: salaryOption,
        label: salaryTypes[salaryOption],
      }));
      setSalaryOptions(translatedSalaryOptions);
    });
  }, [site, language]);

  return salaryOptions;
};

export const formatSalary = ({
  amount,
  localeIntl,
  currencyCode,
}: {
  amount: number;
  localeIntl: string;
  currencyCode: string;
}) =>
  format(amount, {
    currency: currencyCode,
    locale: localeIntl,
    dropFraction: true,
    display: 'noSymbol',
  });

export const getNewSalaryValues = (
  newSalaryFieldValue: string,
  localeIntl: string,
  currencyCode: string,
): { strippedSalaryAmount: number; formattedNewSalaryFieldValue: string } => {
  const strippedSalary = newSalaryFieldValue.trim().replace(/(,|\.)/g, '');
  const strippedSalaryAmount = parseInt(strippedSalary, 10);

  const formattedNewSalaryFieldValue = strippedSalaryAmount
    ? formatSalary({
        amount: strippedSalaryAmount,
        currencyCode,
        localeIntl,
      })
    : '';
  return { strippedSalaryAmount, formattedNewSalaryFieldValue };
};
